import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { AGENT_GEO_ZONE_KEYS } from '../lib';
import { AgentGeoZoneApi } from '../api';
import type {
  AgentGeoZoneBase,
  AgentGeoZoneById,
  AgentGeoZoneCreatePayload,
  AgentGeoZoneUpdatePayload,
  AgentGeoZoneItem,
  AgentGeoZoneItemListParams,
  AgentGeoZonePolygonListItem,
} from './types';

export const useAgentGeoZoneItemList = (params?: AgentGeoZoneItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<AgentGeoZoneItem[]>, Error, AgentGeoZoneItem[] | undefined>({
    queryKey: AGENT_GEO_ZONE_KEYS.itemList(params),
    queryFn: () => AgentGeoZoneApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useAgentGeoZonePolygonList = () => {
  return useQuery<ApiResponse<AgentGeoZonePolygonListItem[]>, Error, AgentGeoZonePolygonListItem[]>({
    queryKey: AGENT_GEO_ZONE_KEYS.polygonList(),
    queryFn: () => AgentGeoZoneApi.getPolygonList(),
    select: (data) => (Array.isArray(data?.result) ? data.result : []),
  });
};

export const useAgentGeoZoneList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<AgentGeoZoneBase>>({
    queryKey: AGENT_GEO_ZONE_KEYS.list(params),
    queryFn: () => AgentGeoZoneApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useAgentGeoZoneById = (id: number) => {
  return useQuery<ApiResponse<AgentGeoZoneById>>({
    queryKey: AGENT_GEO_ZONE_KEYS.detail(id),
    queryFn: () => AgentGeoZoneApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateAgentGeoZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentGeoZoneCreatePayload) => AgentGeoZoneApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_GEO_ZONE_KEYS.all });
    },
  });
};

export const useUpdateAgentGeoZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentGeoZoneUpdatePayload) => AgentGeoZoneApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_GEO_ZONE_KEYS.all });
    },
  });
};

export const useDeleteAgentGeoZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => AgentGeoZoneApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_GEO_ZONE_KEYS.all });
    },
  });
};
